export interface ContractInvolvedEntity {
  entityCode: string;
  signatoryName: string;
  signatoryRole: string;
}

export interface ContractGeneralInfo {
  supplierId: number | null;
  professionalId?: string | null;
  contractType: string | null;
  serviceType: string | null;
  contractSubject: string | null;
  outsourcedActivity: string | null;
  slaKpiPerson: string | null;
  startDate: string | null; // ISO date format: "YYYY-MM-DD"
  endDate: string | null; // ISO date format: "YYYY-MM-DD"
  numberOfRenewals?: number | null;
  automaticRenewal: string | null; // Dropdown: 'YES', 'NO', 'ANNUAL'
  terminationNoticeDays: number | null;
  renewalNoticeDays: number | null; // Changed to string for dropdown compatibility
  contractSectionStatus: 'DRAFT' | 'FILLED'; // Status della compilazione dello step contratto
  status: 'DRAFT' | 'FILLED' | 'GENERATED' | 'ACTIVE' | 'EXPIRING' | 'IN_RENEWAL' | 'EXPIRED' | 'CEASED' | null; // Overall contract status in registry
  involvedEntities: ContractInvolvedEntity[];
  synergies: boolean;
  synergiesCompanies?: string[] | null; // Required when synergies = true
  involvedSc?: string | null;
  supplierType: string | null;
  managementTools?: string[] | null;
  network?: string[] | null;
  contractTemplateId?: string | null;
  contractDocumentsNote?: string | null;
  contractInNumber?: string | null;
}

export interface ContractFrameworkAgreement {
  frameworkAgreement: boolean;
  frameworkStartDate?: string | null; // ISO date format: "YYYY-MM-DD" - Required when frameworkAgreement = true
  frameworkEndDate?: string | null; // ISO date format: "YYYY-MM-DD" - Required when frameworkAgreement = true
  automaticRenewal?: string | null; // Dropdown: 'YES', 'NO', 'ANNUAL' - Required when frameworkAgreement = true
  renewalNoticeDays?: string | null; // Dropdown with predefined day values - Required when frameworkAgreement = true
}

export interface ContractEconomics {
  expectedFee: boolean;
  rebates: boolean;
  rebatesNote?: string | null;
  invoiceOnSol: boolean;
  technicalAccount: boolean;
  allocationMatrix: string | null;
  expenseAccount: string | null;
  currency: string | null;
  totalContractValue: number | null;
  annualValue: number | null;
  invoiceFrequency: string | null;
}

export interface ContractAdministration {
  managingArea: string | null;
  claimsAreaReference: string | null;
  eventualReferent: string | null;
  buyer: string | null;
  involvedStructures: string[];
  invoiceRecipientEmail: string | null;
  controlsOwner: string | null;
  paymentAuthorizer: string | null;
  documentationAtIP: boolean;
  supplierSignatory: string | null;
  supplierSignatoryRole: string | null;
  professionalSignatory: string | null;
  professionalSignatoryRole: string | null;
}

export interface ContractDataProcessing {
  vrfUseCase: string | null;
  gdprDataProcessing: boolean;
  privacyResponsibleFunction?: string | null; // Required when gdprDataProcessing = true
  dataTransferOutsideSee: boolean;
}

export interface ContractDocumentsRequest {
  contractTemplateId: string | null;
  attoDiNominaTemplateId: string | null;
  contractDocumentsNote: string | null;
}

export interface RelatedDocument {
  documentId: number;
  fileName: string;
  uploadDate: string; // ISO date format: "YYYY-MM-DDTHH:mm:ss"
  uploadedBy: string;
  title: string;
  documentS3: string;
}

export enum DocumentType {
  UNSIGNED = 'UNSIGNED',
  EXTERNALLY_SIGNED = 'EXTERNALLY_SIGNED',
  FINAL = 'FINAL'
}

export interface MainDocument {
  s3FileUrl: string;
  uploadedAt: string; // ISO date format: "YYYY-MM-DDTHH:mm:ss"
  uploadedBy: string;
  s3FileUrlUnsigned?: string;
  uploadedAtUnsigned?: string;
  uploadedByUnsigned?: string;
  s3FileUrlExternallySigned?: string;
  uploadedAtExternallySigned?: string;
  uploadedByExternallySigned?: string;
}

export interface Attachment {
  id: number;
  fileName: string;
  description: string;
  s3FileUrl: string;
  uploadedAt: string; // ISO date format: "YYYY-MM-DDTHH:mm:ss"
  uploadedBy: string;
}

export interface ContractRequest {
  generalInfo: ContractGeneralInfo;
  frameworkAgreement: ContractFrameworkAgreement;
  economics: ContractEconomics;
  administration: ContractAdministration;
  dataProcessing: ContractDataProcessing;
}

export interface ContractResponse extends ContractRequest {
  id: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  relatedDocuments?: RelatedDocument[];
  mainDocument?: MainDocument;
  attachments?: Attachment[];
  documentsRequest?: ContractDocumentsRequest; // Template IDs and note for contract documents
  professionalStep?: number | null; // Professional registration step: 1=CONTRACT_SENT, 2=CONTRACT_SIGNATURE, 3=CONTRACT_COUNTERSIGNATURE, 4=COMPLETED
  paymentRatesSectionStatus?: 'DRAFT' | 'FILLED' | null; // Payment rates section status (professional contracts only)
}

// Generic API Response wrapper
export interface ApiResponse<T> {
  status: boolean;
  message: string;
  data: T;
}
